import React, { Fragment, memo, useCallback, useMemo } from 'react';
import { StyleSheet } from 'react-native';

import { useNavigation } from '@react-navigation/native';

import { EventInfo } from '@/api/events/types/types';
import { LineSeparator } from '@/components/LineSeparator';
import { Box } from '@/components/lib/components';
import { PlayerRowSkeleton } from '@/feature/lobby/components/Skeletons/PlayerRowSkeleton';
import { usePickSelection } from '@/feature/lobby/hooks/use-pick-selection';
import { common } from '@/styles/styles';

import { useBetslipData } from '../hooks/use-betslip-data';
import { useBetslipStore } from '../hooks/use-betslip-store';
import { BetslipPick, PlayerWithTeam } from '../types';
import { playersHaveEdgeCombo, sortBetslipItemsByEdgeCombos } from '../utils/betslip-edge-combos';
import { mapPlayerAndEvents } from '../utils/betslip-utils';
import { createPlayerEventKey } from '../utils/markets-cache-store-utils';
import { CheckoutWidget } from './CheckoutWidget';
import { PlayerPickSlipCard } from './PlayerPickSlipCard';

type PlayerRowItemData = {
    item: BetslipPick;
    nextPlayer?: BetslipPick;
    isEdgeCombo: boolean;
    eventDetails?: EventInfo;
    isLastItem: boolean;
};

type PlayerRowItemProps = PlayerRowItemData & {
    loading: boolean;
    makeSelection: (pick: BetslipPick, analyticsTag?: string) => boolean;
    removeSelection: (
        listToRemove: {
            eventId: string;
            player: PlayerWithTeam;
        }[]
    ) => void;
    handleOpenPlayerProfile: (playerId: string) => void;
};

const PlayerRowItem = memo<PlayerRowItemProps>(
    ({
        item,
        nextPlayer,
        isEdgeCombo,
        eventDetails,
        isLastItem,
        loading,
        makeSelection,
        removeSelection,
        handleOpenPlayerProfile,
    }) => {
        if (loading) {
            return (
                <Fragment>
                    <PlayerRowSkeleton mode={'pickslip'} showSeparator={true} paddingVertical={'s0'} />
                    {!isLastItem ? <LineSeparator style={[common.hairlineHeight, styles.separator]} /> : null}
                </Fragment>
            );
        }

        if (!eventDetails) {
            return null;
        }

        return (
            <Box>
                <PlayerPickSlipCard
                    presentation={'sheet'}
                    {...item}
                    eventDetails={eventDetails}
                    makeSelection={makeSelection}
                    openPlayerProfile={handleOpenPlayerProfile}
                    removeSelection={removeSelection}
                    shouldDisplayEdgeComboIcon={isEdgeCombo}
                    shouldDisplaySeparator={!isLastItem}
                    edgeComboPlayers={isEdgeCombo && nextPlayer ? [item, nextPlayer] : undefined}
                />
            </Box>
        );
    }
);

export const Lineup = memo(({ events, loading }: { events: EventInfo[] | undefined; loading: boolean }) => {
    const betslip = useBetslipStore(state => state.betslip);
    const { edgeCombos } = useBetslipData();

    const navigation = useNavigation();

    const { makeSelection, removeSelection } = usePickSelection();

    const sortedBetslip = useMemo(() => sortBetslipItemsByEdgeCombos(betslip, edgeCombos), [betslip, edgeCombos]);

    const playersWithEvent = useMemo(() => mapPlayerAndEvents(sortedBetslip, events), [sortedBetslip, events]);

    const handleOpenPlayerProfile = useCallback(
        (playerId: string) => {
            const player = playersWithEvent.find(p => p.id === playerId);
            if (!player) {
                return;
            }

            navigation.navigate('SinglePlayerProfileModalScreen', { player });
        },
        [navigation, playersWithEvent]
    );

    // Prepare data with edge combo and event info for efficient rendering
    const listData = useMemo(() => {
        return sortedBetslip.map((item, index): PlayerRowItemData => {
            const nextPlayer = sortedBetslip[index + 1];
            const isEdgeCombo = playersHaveEdgeCombo(edgeCombos, item.player, nextPlayer?.player);
            const eventDetails = events?.find(e => e.id === item.eventId);

            return {
                item,
                nextPlayer,
                isEdgeCombo,
                eventDetails,
                isLastItem: index === sortedBetslip.length - 1,
            };
        });
    }, [sortedBetslip, edgeCombos, events]);

    const keyExtractor = useCallback(
        (item: PlayerRowItemData) => createPlayerEventKey(item.item.player.id, item.item.eventId),
        []
    );

    if (betslip.length === 0) {
        return null;
    }

    return (
        <CheckoutWidget>
            {listData.map(item => (
                <PlayerRowItem
                    key={keyExtractor(item)}
                    {...item}
                    loading={loading}
                    makeSelection={makeSelection}
                    removeSelection={removeSelection}
                    handleOpenPlayerProfile={handleOpenPlayerProfile}
                />
            ))}
        </CheckoutWidget>
    );
});

const styles = StyleSheet.create({
    separator: {
        marginVertical: 16,
    },
});
